
import React from 'react';
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TranslationText } from '@/components/i18n/TranslationText';
import { useTranslation } from 'react-i18next';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            <TranslationText i18nKey="backToHome">Back to Home</TranslationText>
          </Button>
          
          <h1 className="text-4xl font-bold mb-4">
            <TranslationText i18nKey="privacyPolicy">Privacy Policy</TranslationText>
          </h1>
          
          <p className="text-muted-foreground">
            <TranslationText i18nKey="lastUpdated">Last updated: January 2024</TranslationText>
          </p>
        </div>
        
        <div className="prose prose-gray dark:prose-invert max-w-none space-y-8">
          <div>
            <h2 className="text-2xl font-semibold mb-4">
              <TranslationText i18nKey="privacy.dataCollection">1. Information We Collect</TranslationText>
            </h2>
            <p className="text-muted-foreground">
              <TranslationText i18nKey="privacy.dataCollectionText">
                We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support. This includes your name, email address, and contract data you upload to our platform.
              </TranslationText>
            </p>
          </div>
          
          <div>
            <h2 className="text-2xl font-semibold mb-4">
              <TranslationText i18nKey="privacy.dataUse">2. How We Use Your Information</TranslationText>
            </h2>
            <p className="text-muted-foreground">
              <TranslationText i18nKey="privacy.dataUseText">
                We use the information we collect to provide, maintain, and improve our services, process transactions, send you technical notices and support messages, and communicate with you about products, services, and promotional offers.
              </TranslationText>
            </p>
          </div>
          
          <div>
            <h2 className="text-2xl font-semibold mb-4">
              <TranslationText i18nKey="privacy.dataSharing">3. Information Sharing</TranslationText>
            </h2>
            <p className="text-muted-foreground">
              <TranslationText i18nKey="privacy.dataSharingText">
                We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy. We may share your information with trusted service providers who assist us in operating our platform.
              </TranslationText>
            </p>
          </div>
          
          <div>
            <h2 className="text-2xl font-semibold mb-4">
              <TranslationText i18nKey="privacy.dataSecurity">4. Data Security</TranslationText>
            </h2>
            <p className="text-muted-foreground">
              <TranslationText i18nKey="privacy.dataSecurityText">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. All data is encrypted in transit and at rest.
              </TranslationText>
            </p>
          </div>
          
          <div>
            <h2 className="text-2xl font-semibold mb-4">
              <TranslationText i18nKey="privacy.dataRetention">5. Data Retention</TranslationText>
            </h2>
            <p className="text-muted-foreground">
              <TranslationText i18nKey="privacy.dataRetentionText">
                We retain your personal information for as long as necessary to provide you with our services and as described in this privacy policy. You may request deletion of your account and associated data at any time.
              </TranslationText>
            </p>
          </div>
          
          <div>
            <h2 className="text-2xl font-semibold mb-4">
              <TranslationText i18nKey="privacy.userRights">6. Your Rights</TranslationText>
            </h2>
            <p className="text-muted-foreground">
              <TranslationText i18nKey="privacy.userRightsText">
                You have the right to access, update, or delete your personal information. You may also object to or restrict certain processing of your data. To exercise these rights, please contact us using the information provided below.
              </TranslationText>
            </p>
          </div>
          
          <div>
            <h2 className="text-2xl font-semibold mb-4">
              <TranslationText i18nKey="privacy.cookies">7. Cookies and Tracking</TranslationText>
            </h2>
            <p className="text-muted-foreground">
              <TranslationText i18nKey="privacy.cookiesText">
                We use cookies and similar tracking technologies to collect and track information and to improve and analyze our service. You can control cookies through your browser settings.
              </TranslationText>
            </p>
          </div>
          
          <div className="pt-8 border-t">
            <h2 className="text-2xl font-semibold mb-4">
              <TranslationText i18nKey="contactUs">Contact Us</TranslationText>
            </h2>
            <p className="text-muted-foreground">
              <TranslationText i18nKey="privacy.contactText">
                If you have any questions about this privacy policy or our data practices, please contact us at privacy@subtrackr.com or through our support channels.
              </TranslationText>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
